import type { CoreRequest, IdentityRequest } from '$api-clients/hooks';
import { mapTeamMemberToAgentProfile } from './mapTeamMemberToAgentProfile';
import type { AgentProfileFields, ContactLicenseInfo } from './types';

type TeamMemberDetail =
  IdentityRequest<'/teams/{teamId}/members/{userId}'>['Data'];
type TeamDetail = IdentityRequest<'/teams/{teamId}'>['Data'];
type MemberBrokerage =
  IdentityRequest<'/teams/{teamId}/members/{userId}/brokerage'>['Data'];
type MlsMemberships =
  IdentityRequest<'/teams/{teamId}/mls-memberships'>['Data']['data'];
type TeamMemberPreferences =
  CoreRequest<'/user-settings/{id}/team-member-preferences/{teamId}'>['Data'];

export interface AgentProfileSources {
  member: TeamMemberDetail;
  team: TeamDetail;
  /**
   * The member-scoped brokerage: the team's brokerage, or the market's LFRO
   * brokerage for referral agents.
   */
  brokerage: MemberBrokerage | null;
  mlsMemberships: MlsMemberships | null;
  preferences: TeamMemberPreferences | null;
}

const officeManagerInfo = (
  member: TeamMemberDetail,
  team: TeamDetail,
): ContactLicenseInfo => {
  const supervisingBrokerActsAsOfficeManager =
    !team.officeManager || member.userId === team.officeManager.userId;

  const actingOfficeManager = supervisingBrokerActsAsOfficeManager
    ? team.supervisingBroker
    : team.officeManager;

  return {
    cellPhone: actingOfficeManager?.contact.phone ?? null,
    email: actingOfficeManager?.contact.email ?? null,
    firstName: actingOfficeManager?.firstName ?? null,
    lastName: actingOfficeManager?.lastName ?? null,
    licenseNumber: actingOfficeManager?.license?.number ?? null,
  };
};

export function mapToAgentProfile({
  member,
  team,
  brokerage,
  mlsMemberships,
  preferences,
}: AgentProfileSources): AgentProfileFields {
  const summary = mapTeamMemberToAgentProfile({ ...member, team });
  const primaryMlsMembership = mlsMemberships?.find((m) => m.primary);

  // Referral agents transact under their LFRO brokerage, not the team.
  const isReferralAgent = member.roles.includes('REFERRAL_AGENT');

  return {
    ...summary,
    companyName: isReferralAgent
      ? (brokerage?.name ?? null)
      : summary.companyName,
    brokerageLicenseNumber: brokerage?.license.number ?? null,
    cellPhone: member.contact.phone ?? null,
    // Despite these being top-level attributes, the address maps to the team address.
    street1: team.registeredAddress?.street1 ?? null,
    street2: team.registeredAddress?.street2 ?? null,
    city: team.registeredAddress?.city ?? null,
    state: team.registeredAddress?.state ?? null,
    zipCode: team.registeredAddress?.postalCode ?? null,
    licenseNumber: member.license?.number ?? null,
    brokerageCellPhone: team.phoneNumber ?? null,
    brokerageEmail: team.email ?? null,
    brokerageMlsId: primaryMlsMembership?.registrationNumber ?? null,
    brokerageMlsCode: primaryMlsMembership?.registrationNumber ?? null,
    brokerageOfficeEmail: team.mlsOfficeEmail ?? null,
    supervisingBroker: {
      firstName: team.supervisingBroker?.firstName ?? null,
      lastName: team.supervisingBroker?.lastName ?? null,
      licenseNumber: team.supervisingBroker?.license?.number ?? null,
      cellPhone: team.supervisingBroker?.contact.phone ?? null,
      email: team.supervisingBroker?.contact.email ?? null,
    },
    broker: {
      firstName: brokerage?.managingBroker?.firstName ?? null,
      lastName: brokerage?.managingBroker?.lastName ?? null,
      licenseNumber: brokerage?.managingBroker?.license?.number ?? null,
      cellPhone: brokerage?.managingBroker?.contact.phone ?? null,
      email: brokerage?.managingBroker?.contact.email ?? null,
    },
    officeManager: officeManagerInfo(member, team),
    agentCommission: preferences?.agentCommission ?? null,
    sellingAgentCommission: preferences?.sellingAgentCommission ?? null,
    mlsId: primaryMlsMembership?.registrationNumber ?? null,
  };
}
